import api from "../api/axiosInstance";
import { setToken } from "../utils/token";
import { setTenantId, clearTenantId, getTenantId } from "../utils/tenant";
import { pickProfileImageUrl } from "../utils/profileImage";

const CURRENT_USER_KEY = "currentUser";

// Exchange Google credential (ID token) for app session
export async function loginWithGoogleCredential(credential, mfaCode) {
	try {
		const attempts = [
			{ url: "/auth/google", body: { credential } },
			{ url: "/auth/google/login", body: { credential } },
			{ url: "/auth/login/google", body: { credential } },
			{ url: "/auth/social/google", body: { credential } },
			{ url: "/auth/oauth/google", body: { id_token: credential } },
		];
		const normalizedMfa = String(mfaCode || "").trim();
		let resp;
		let lastErr;
		for (const a of attempts) {
			try {
				const body = { ...(a.body || {}) };
				if (normalizedMfa) body.mfa_code = normalizedMfa;
				resp = await api.post(a.url, body, { params: { check_mfa: true } });
				break;
			} catch (e) {
				// If backend is explicitly asking for MFA, do not continue trying other endpoints;
				// surface it to the UI so it can prompt for the code.
				const status = e?.response?.status;
				const backendMsg = e?.response?.data?.message || e?.message || "";
				const explicitMfa =
					e?.code === "MFA_REQUIRED" ||
					e?.response?.data?.code === "MFA_REQUIRED" ||
					e?.response?.data?.mfa_required === true ||
					(e?.response?.headers && String(e.response.headers["x-mfa-required"]).toLowerCase() === "true") ||
					((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
				if (explicitMfa) {
					const err = new Error("Multi-Factor Authentication (MFA) is required");
					err.code = "MFA_REQUIRED";
					err.response = e?.response || { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
					throw err;
				}
				lastErr = e;
				continue;
			}
		}
		if (!resp) throw lastErr || new Error("Google login failed");
		const data = resp?.data || {};
		// Persist tenant if provided
		try {
			const tenantFromHeader =
				resp?.headers?.["x-tenant-id"] || resp?.headers?.["X-Tenant-ID"];
			const tenantFromBody = data?.tenant_id || data?.tenantId;
			const resolvedTenant = String(tenantFromHeader || tenantFromBody || "").trim();
			if (resolvedTenant) setTenantId(resolvedTenant);
		} catch {}
		// Persist token
		const token = data?.access_token || data?.accessToken || data?.token || data?.jwt;
		if (token) setToken(token);

		// If backend indicates MFA is required and no token is returned, surface an error so UI can prompt for code
		if (!token) {
			const explicitMfaRequired =
				data?.mfa_required === true ||
				data?.require_mfa === true ||
				data?.requires_mfa === true;
			if (explicitMfaRequired) {
				const err = new Error("Multi-Factor Authentication (MFA) is required");
				err.code = "MFA_REQUIRED";
				err.response = { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
				throw err;
			}
		}

		// Persist user profile (for avatar/name/email UI)
		try {
			if (data?.user && typeof data.user === "object") {
				const u = { ...data.user };
				const img = pickProfileImageUrl(u);
				if (img) {
					if (!u.profile_picture && !u.profilePicture) u.profile_picture = img;
					if (!u.image) u.image = img;
				}
				localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(u));
			}
		} catch {}
		return data;
	} catch (e) {
		// Normalize common MFA-required responses (when backend rejects without token)
		const status = e?.response?.status;
		const backendMsg = e?.response?.data?.message || e?.message || "";
		const explicitMfa =
			e?.code === "MFA_REQUIRED" ||
			e?.response?.data?.code === "MFA_REQUIRED" ||
			e?.response?.data?.mfa_required === true ||
			(e?.response?.headers && String(e.response.headers["x-mfa-required"]).toLowerCase() === "true") ||
			((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
		if (explicitMfa && e?.code !== "MFA_REQUIRED") {
			const err = new Error("Multi-Factor Authentication (MFA) is required");
			err.code = "MFA_REQUIRED";
			err.response = e?.response || { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
			throw err;
		}
		throw e;
	}
}

export async function authenticateWithRegister(email, password) {
	const payload = { email, password };
	const { data } = await api.post("/auth/register", payload);
	if (data?.token) {
		setToken(data.token);
	}
	return data;
}

export async function discoverTenant(email) {
	const payload = { email };
	const { data } = await api.post("/auth/discover-tenant", payload);
	// Expected shape: { email, tenant_id, found, message }
	if (data?.tenant_id) {
		setTenantId(data.tenant_id);
	} else {
		clearTenantId();
	}
	return data;
}

// Public endpoint to check MFA status for the account (no auth required)
export async function getMfaStatus(email) {
	try {
		// Prefer GET with query param
		const { data } = await api.get("/auth/mfa/status", { params: { email } });
		return data;
	} catch (e) {
		// Fallback to POST { email }
		const { data } = await api.post("/auth/mfa/status", { email });
		return data;
	}
}

export async function loginWithCredentials(email, password, mfaCode) {
	// 1) Discover tenant for the provided email
	const discovery = await discoverTenant(email);
	const tenantId = discovery?.tenant_id || getTenantId();
	if (!discovery?.found || !tenantId) {
		const msg = discovery?.message || "Tenant not found for this email";
		const err = new Error(msg);
		err.code = "TENANT_NOT_FOUND";
		throw err;
	}

	// 2) Proceed with login, ensuring tenant header is sent
	const payload = { email, password };
	if (mfaCode) payload.mfa_code = String(mfaCode).trim();
	const resp = await api.post("/auth/login", payload, {
		headers: { "X-Tenant-ID": tenantId },
		params: { check_mfa: true }
	});
	const data = resp?.data || {};
	// Persist/refresh tenant id from authoritative response headers/body
	try {
		const tenantFromHeader =
			resp?.headers?.["x-tenant-id"] ||
			resp?.headers?.["X-Tenant-ID"];
		const tenantFromBody = data?.tenant_id || data?.tenantId;
		const resolvedTenant = String(tenantFromHeader || tenantFromBody || tenantId || "").trim();
		if (resolvedTenant) setTenantId(resolvedTenant);
	} catch {}

	// If backend indicates MFA is required and no token is returned, surface an error so UI can prompt for code
	const tokenInPayload = data?.access_token || data?.accessToken || data?.token || data?.jwt;
	if (!tokenInPayload) {
		const explicitMfaRequired =
			data?.mfa_required === true ||
			data?.require_mfa === true ||
			data?.requires_mfa === true;
		if (explicitMfaRequired) {
			const err = new Error("Multi-Factor Authentication (MFA) is required");
			err.code = "MFA_REQUIRED";
			err.response = { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
			throw err;
		}
	}
	const token = tokenInPayload;
	if (token) {
		setToken(token);
	}
	// Persist user profile if backend returns it
	try {
		if (data?.user && typeof data.user === "object") {
			const u = { ...data.user };
			const img = pickProfileImageUrl(u);
			if (img) {
				if (!u.profile_picture && !u.profilePicture) u.profile_picture = img;
				if (!u.image) u.image = img;
			}
			localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(u));
		}
	} catch {}
	// Persist role if backend provides it
	try {
		const rolesFromRoot = Array.isArray(data?.roles) ? data.roles : null;
		const rolesFromUser = Array.isArray(data?.user?.roles) ? data.user.roles : null;
		const role =
			(data?.role ||
			 data?.user?.role ||
			 (rolesFromRoot && rolesFromRoot.length ? rolesFromRoot[0] : "") ||
			 (rolesFromUser && rolesFromUser.length ? rolesFromUser[0] : "") ||
			 (data?.user?.is_admin ? "admin" : "") ||
			 "").toString().toLowerCase();
		if (role) localStorage.setItem("role", role);
	} catch {}
	return data;
}

export async function sendVerificationCode(email) {
	const { data } = await api.post("/auth/send-verification-code", { email });
	return data;
}

export async function verifyEmailCode(email, code) {
	const { data } = await api.post("/auth/verify-code", { email, code });
	// Persist verification token for the register step if provided
	const tokenFromApi = data?.verification_token || data?.verificationToken || data?.token;
	if (tokenFromApi) {
		try { localStorage.setItem("verificationToken", tokenFromApi); } catch (_) {}
	}
	return data;
}

export async function registerUser({ email, name, password, verificationToken }) {
	// Backend expects: { email, name, password, verification_token }
	let token = verificationToken;
	if (!token) {
		try { token = localStorage.getItem("verificationToken") || ""; } catch (_) { token = ""; }
	}
	const payload = {
		email,
		name,
		password,
		verification_token: token
	};
	const { data } = await api.post("/auth/register", payload);
	return data;
}

// Forgot password: send reset code to email
export async function requestPasswordReset(email) {
	const { data } = await api.post("/auth/forgot-password", { email });
	return data;
}

// Verify reset code; backend may return a token
export async function verifyResetCode(email, code) {
	// Try common payload variants in case the backend expects different keys
	const normalizedCode = String(code || "").trim();
	const numericCode = /^\d+$/.test(normalizedCode) ? Number(normalizedCode) : normalizedCode;
	const attempts = [
		{ email, code: normalizedCode },
		{ email, code: numericCode },
		{ email, verification_code: normalizedCode },
		{ email, verification_code: numericCode },
		{ code: normalizedCode, email },
		{ verification_code: normalizedCode, email },
		{ email, code: normalizedCode, purpose: "password_reset" },
		{ email, verification_code: normalizedCode, purpose: "password_reset" },
		{ email, reset_code: normalizedCode },
		{ email, otp: normalizedCode },
		{ email, one_time_code: normalizedCode },
		{ email, channel: "email", code: normalizedCode },
	];
	let lastErr;
	for (const body of attempts) {
		try {
			const { data } = await api.post("/auth/verify-code", body);
			const tokenFromApi = data?.verification_token || data?.verificationToken || data?.token;
			if (tokenFromApi) {
				try { localStorage.setItem("passwordResetToken", tokenFromApi); } catch (_) {}
			}
			return data;
		} catch (e) {
			lastErr = e;
			// try next variant on 400/404/422
			const status = e?.response?.status;
			if (status !== 400 && status !== 404 && status !== 422) break;
		}
	}
	throw lastErr;
}

// Reset password using email + code + new_password
export async function resetPassword(email, code, new_password) {
	const payload = { email, code: String(code || "").trim(), new_password };
	const { data } = await api.post("/auth/reset-password", payload);
	return data;
}


